import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types';
import toast from 'react-hot-toast';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    flowType: 'implicit',
    storage: window.localStorage,
    storageKey: 'gig-auth-token'
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web/2.39.7'
    }
  }
});

// Track connection state
let isConnected = true;

// Function to handle reconnection
export const reconnect = async () => {
  try {
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      // If no session, redirect to login
      window.location.href = '/login';
      return false;
    }

    // Test connection with a simple query
    const { error: testError } = await supabase
      .from('users')
      .select('id')
      .limit(1)
      .single();

    if (testError && !testError.message.includes('No rows found')) {
      throw testError;
    }

    // Reinitialize realtime connection
    await supabase.removeAllChannels();
    initializeRealtimeConnection();
    
    isConnected = true;
    toast.success('Connection restored');
    return true;
  } catch (error) {
    console.error('Reconnection failed:', error);
    toast.error('Failed to reconnect. Please try again.');
    isConnected = false;
    
    // If auth error, redirect to login
    if (error.message?.includes('auth') || error.message?.includes('session')) {
      window.location.href = '/login';
    }
    
    return false;
  }
};

// Initialize realtime connection
const initializeRealtimeConnection = () => {
  supabase.channel('system')
    .on('system', { event: 'disconnected' }, () => {
      isConnected = false;
      toast.error('Connection lost');
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        isConnected = true;
      }
    });
};

// Initialize the realtime connection
initializeRealtimeConnection();

// Handle visibility changes
let lastVisibilityChange = 0;
document.addEventListener('visibilitychange', () => {
  const now = Date.now();
  
  // Prevent multiple reloads within 2 seconds
  if (now - lastVisibilityChange < 2000) {
    return;
  }
  
  lastVisibilityChange = now;

  if (document.visibilityState === 'visible') {
    // Reload the page when switching back to the tab
    window.location.reload();
  }
});